import { Autocomplete, Checkbox } from '@mui/material';
import CustomTextField from './CustomTextField';
import onlineIcon from 'src/assets/icons/onlineIcon.svg';
import inpersonIcon from 'src/assets/icons/InpersonIcon.svg';
import onlineAndInperson from 'src/assets/icons/onlineAndInperson.svg';
import './AutocompleteDropdown.scss';
import { MeetingType } from 'src/latest/src/constants/MeetingTypeEnums';

const AutocompleteDropdown = ({
  type,
  index,
  filter,
  onFilterValueUpdated,
  className,
  disabled,
  isGrouped = false, // New prop to control grouping
  ...props
}) => {

  const getSlotIcons = (slotReason, customClassName) => {
    if (
      slotReason.toString().toLowerCase().includes(MeetingType?.Online.toLowerCase()) &&
      !slotReason.toString().toLowerCase().includes(MeetingType?.OnlineOrInPerson.toLowerCase()) &&
      !slotReason.toString().toLowerCase().includes(MeetingType?.InPerson.toLowerCase())
    ) {
      return <img src={onlineIcon} alt="onlineIcon" className={customClassName} />;
    }

    if (
      slotReason.toString().toLowerCase().includes(MeetingType?.InPerson.toLowerCase()) &&
      !slotReason.toString().toLowerCase().includes(MeetingType?.Online.toLowerCase()) &&
      !slotReason.toString().toLowerCase().includes(MeetingType?.OnlineOrInPerson.toLowerCase())
    ) {
      return <img src={inpersonIcon} alt="inpersonIcon" className={customClassName} />;
    }

    if (
      slotReason.toString().toLowerCase().includes(MeetingType?.OnlineOrInPerson.toLowerCase())
    ) {
      return (
        <img src={onlineAndInperson} alt="onlineAndInperson" className={customClassName} />
      );
    }
  };

  // Define preferred group order
  const preferredGroupOrder = [
    'Commonly Selected Expertise',
    'Additional Expertise',
    'Conmmonly Selected Approaches',
    'Additional Approaches',
  ];

  // Prepare options and include grouping logic if isGrouped is true
  const options = isGrouped
    ? filter.filter_selection_data
      .flatMap(el =>
        el.group.map(group => ({
          ...el,
          value: el.unique_key,
          title: el.label,
          group,
        })),
      )
      // Sort by preferred group order
      .sort(
        (a, b) =>
          preferredGroupOrder.indexOf(a.group) - preferredGroupOrder.indexOf(b.group) ||
          a.title.localeCompare(b.title), // Secondary sort by title
      )
    : filter.filter_selection_data.map(el => ({
      ...el,
      value: el.unique_key,
      title: el.label,
    }));

  return (
    <Autocomplete
      {...props}
      multiple={type === 'multi-checkbox'}
      disabled={disabled}
      size="small"
      disablePortal={true}
      disableClearable={filter.filter_key === 'serviceType'}
      disableCloseOnSelect={type === 'multi-checkbox'}
      key={`autocomplete-${index}`}
      defaultValue={
        type === 'multi-checkbox'
          ? Array.from(
            new Set(
              options
                .filter(option => option.selected)
                .map(option => option.unique_key)
            )
          ).map(uniqueKey =>
            options.find(option => option.unique_key === uniqueKey)
          ) // Deduplicate default selected options
          : options.find(option => option.selected)
      }
      id={`autocomplete-${index}`}
      onChange={(event, newValue) => {
        // Deduplicate selected options based on unique_key

        let uniqueNewValue;

        if (type === 'multi-checkbox') {
          uniqueNewValue = newValue.filter(
            (option, index, self) =>
              index === self.findIndex(o => o.unique_key === option.unique_key)
          );
        }

        const selectedWithOriginalGroups =
          type === 'multi-checkbox'
            ? uniqueNewValue.map(option => ({
              ...option,
              group: isGrouped ? [option.group] : option.group, // Convert string to array only when isGrouped is true
            }))
            : newValue;

        onFilterValueUpdated(filter, selectedWithOriginalGroups);
      }}
      options={options}
      isOptionEqualToValue={(option, value) =>
        option.unique_key === value.unique_key
      } // Compare unique_key for equality
      groupBy={isGrouped ? option => option.group : undefined} // Apply grouping if isGrouped is true
      className={`ca-filter-autocomplete ${className}`}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          {type === 'multi-checkbox' && (
            <Checkbox
              style={{ marginRight: 8 }}
              checked={selected}
              color="success"
            />
          )}
          {filter.filter_key === 'meetingPreference' && getSlotIcons(option.title, "me-2")}
          {option.title}
        </li>
      )}
      renderInput={params => (
        <CustomTextField
          variant="filled"
          {...params}
          label={filter?.filter_label}
          InputProps={
            filter?.filter_key === 'meetingPreference' && params.inputProps?.value
              ? {
                ...params.InputProps,
                startAdornment: (
                  <>
                    {getSlotIcons(params.inputProps.value, "me-1 custom-icon-ml")}
                    {params.InputProps?.startAdornment}
                  </>
                ),
              }
              : params.InputProps
          }
        />
      )}
      renderGroup={params => (
        <li key={params.key}>
          <h6 className="ms-3 mt-2 group-header">{params.group}</h6>
          <p>{params.children}</p>
        </li>
      )}
    />
  );
};

export default AutocompleteDropdown;